import React, { useEffect, useRef } from "react"
import { MenuItem, Skeleton, Stack, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { PriceDisplay } from "../PriceDisplay"
import { TokenAvatar } from "../TokenAvatar"
import { PRE_CACHED_TOKENS } from "@/hooks/use-token-info"
import { $tokenInfoCache } from "@/stores/token-info-store"
import { SearchOption } from "../RootLayout/SearchOption"
import TokenDisplay from "./TokenDisplay"
import type {
  SearchResultsExactMatch,
  SearchResultsGroupedPool,
  SearchResultsOptions,
} from "@/hooks/useSearchResults"
import type { TokenInfo } from "@/api/token-api"

interface SearchResultsProps {
  options: SearchResultsOptions
  groupedPools: Record<string, SearchResultsGroupedPool>
  exactMatch: SearchResultsExactMatch | null // new prop for exact match
  handleClose: () => void
  loading: boolean
}

export function SearchResults({
  options,
  groupedPools,
  exactMatch,
  handleClose,
  loading,
}: SearchResultsProps) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />
      </Stack>
    )
  }

  return (
    <Stack direction="column" gap={1}>
      {/* Render the exact match result, if one exists */}
      {exactMatch && <ExactMatchResult exactMatch={exactMatch} handleClose={handleClose} />}

      <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
        {options.tokens.map((token) => (
          <MenuItem
            key={token.id}
            onClick={() => {
              handleClose()
              navigate(`/token/${token.id}`)
            }}
            sx={{
              padding: 1,
              border: "1px solid var(--mui-palette-divider)",
              borderRadius: 1,
            }}
          >
            <TokenDisplay token={token} />
          </MenuItem>
        ))}
      </Stack>
      <Stack spacing={2} mt={4}>
        {Object.keys(groupedPools).length === 0 ? (
          <Typography variant="body2">No pools found.</Typography>
        ) : (
          Object.keys(groupedPools).map((groupKey) => {
            const group = groupedPools[groupKey]
            return (
              <Stack key={groupKey}>
                {/* Header for each group */}
                <PoolGroupHeader group={group} />
                <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
                  {group.pools.map((pool) => (
                    <SearchOption key={pool.id} pool={pool} handleClose={handleClose} />
                  ))}
                </Stack>
              </Stack>
            )
          })
        )}
      </Stack>
    </Stack>
  )
}

interface ExactMatchResultProps {
  exactMatch: SearchResultsExactMatch
  handleClose: () => void
}

function ExactMatchResult({ exactMatch, handleClose }: ExactMatchResultProps) {
  const navigate = useNavigate()
  const exactMatchRef = useRef<HTMLLIElement>(null)

  // Focus the exact match item when it mounts.
  useEffect(() => {
    exactMatchRef.current?.focus()
  }, [])

  // Handle Enter key: navigate based on the match type.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (exactMatch.type === "pool") {
        handleClose()
        navigate(`/pool/${(exactMatch.data as { id: string }).id}`)
      } else if (exactMatch.type === "token") {
        handleClose()

        const token = (exactMatch.data as { token: { id: string } }).token
        navigate(`/token/${token}`)
      }
      e.preventDefault()
    }
  }

  // Common style for both pool and token exact matches
  const exactMatchSx = {
    position: "relative",
    // Negative margin to slide it up underneath the search bar
    mt: "-12px",
    // Extra padding on top to offset the negative margin
    pt: "20px",
    pb: "12px",
    px: 2,
    borderRadius: "0 0 8px 8px",
    // Subtle gradient background
    background: `linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.04) 0%,
      rgba(255, 255, 255, 0) 100%
    )`,
    // A bit of shadow and blur to give it a “lifted” feel
    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
    backdropFilter: "blur(4px)",
    border: "1px solid",
    borderColor: "rgba(255,255,255,0.2)",
    color: "text.primary",
    zIndex: 10, // Ensure it sits above other elements

    // Hover and focus states for a polished UI
    "&:hover": {
      background: `linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.06) 0%,
        rgba(255, 255, 255, 0) 100%
      )`,
      cursor: "pointer",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "0 0 0 2px rgba(255,255,255,0.2)",
    },
  } as const

  if (exactMatch.type === "pool") {
    const pool = exactMatch.data as { id: string }
    return (
      <MenuItem
        ref={exactMatchRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={() => {
          handleClose()
          navigate(`/pool/${pool.id}`)
        }}
        sx={exactMatchSx}
      >
        <Typography variant="body1" fontWeight={600}>
          Open Pool {pool.id} page
        </Typography>
      </MenuItem>
    )
  }

  if (exactMatch.type === "token") {
    const { token } = exactMatch.data as { token: TokenInfo }
    return (
      <MenuItem
        ref={exactMatchRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={() => {
          handleClose()
          navigate(`/token/${token.id}`)
        }}
        sx={exactMatchSx}
      >
        <Stack direction="row" gap={1} alignContent="center">
          <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
            Open Token
          </Typography>
          <TokenDisplay token={token} /> Page
        </Stack>
      </MenuItem>
    )
  }

  if (exactMatch.type === "entity") {
    const entity = exactMatch.data as string
    return (
      <MenuItem
        ref={exactMatchRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={() => {
          handleClose()
          navigate(`/entity/${entity}`)
        }}
        sx={exactMatchSx}
      >
        <Typography variant="body1" fontWeight={600}>
          Open Entity {entity} page
        </Typography>
      </MenuItem>
    )
  }

  return null
}
function PoolGroupHeader({ group }: { group: SearchResultsGroupedPool }) {
  const tokenInfoCache = $tokenInfoCache.get() // Ensure proper import if used in this context
  const baseTokenInfo =
    group.tokenIds[0] in PRE_CACHED_TOKENS
      ? PRE_CACHED_TOKENS[group.tokenIds[0]]
      : JSON.parse(tokenInfoCache[group.tokenIds[0]] as string)
  const quoteTokenInfo =
    group.tokenIds[1] in PRE_CACHED_TOKENS
      ? PRE_CACHED_TOKENS[group.tokenIds[1]]
      : JSON.parse(tokenInfoCache[group.tokenIds[1]] as string)

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" sx={{ pt: "3px" }} alignItems="center" gap={0.5}>
        <TokenAvatar tokenId={group.tokenIds[0]} />
        <TokenAvatar tokenId={group.tokenIds[1]} />
        <Typography component="span" variant="inherit" fontWeight={500}>
          {baseTokenInfo?.ticker}
        </Typography>
        <Typography component="span" color="text.secondary" variant="inherit" fontWeight={300}>
          /{quoteTokenInfo?.ticker}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption">Total Liquidity:</Typography>
        <PriceDisplay amount={group.totalLiquidity} />
      </Stack>
    </Stack>
  )
}
